import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, LogOut, Download, XCircle, X, Check, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GATE_PASS_STATUS_META, FUEL_LEVELS, inr } from "./lib";
import { downloadGatePassPdf } from "@/lib/pdf";
import WorkflowStrip from "@/components/WorkflowStrip";

export default function GatePassDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { garage } = useAuth();
  const [gp, setGp] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exitOpen, setExitOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/billing/gate-passes/${id}`);
      setGp(r.data);
      const a = await api.get(`/billing/gate-passes/${id}/audit`);
      setAudit(a.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load gate pass");
      nav("/gate-pass");
    } finally { setLoading(false); }
  }, [id, nav]);

  useEffect(() => { load(); }, [load]);

  const printPdf = async () => {
    if (!gp) return;
    try { downloadGatePassPdf(gp, garage); } catch (e) { toast.error("Could not build PDF"); }
  };

  if (loading || !gp) return <div className="py-10 text-center text-[13px] text-[#667085]">Loading…</div>;

  const st = GATE_PASS_STATUS_META[gp.status] || GATE_PASS_STATUS_META.draft;
  const canMarkExit = gp.status === "issued";
  const canCancel = ["issued"].includes(gp.status);

  return (
    <div className="space-y-4" data-testid="gp-detail-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/gate-pass" data-testid="gp-detail-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight font-mono">{gp.number}</h1>
              <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider ${st.chip}`}
                    data-testid="gp-detail-status">{st.label}</span>
              {gp.override_used && (
                <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#e63946]/12 text-[#e63946]"
                      data-testid="gp-detail-override-chip">
                  Override
                </span>
              )}
            </div>
            <div className="text-[12.5px] text-[#667085] mt-0.5">
              {gp.customer_snapshot?.name} · {gp.vehicle_snapshot?.reg_number}
              {gp.invoice_number && <> · Invoice <Link to={`/invoices/${gp.invoice_id}`} className="font-mono text-[#457b9d] hover:text-[#e63946]">{gp.invoice_number}</Link></>}
              {gp.job_card_number && <> · JC <Link to={`/job-cards/${gp.job_card_id}`} className="font-mono text-[#457b9d] hover:text-[#e63946]">{gp.job_card_number}</Link></>}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={printPdf} data-testid="gp-print"
                  className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
            <Download size={13} /> PDF
          </button>
          {canMarkExit && (
            <button onClick={() => setExitOpen(true)} data-testid="gp-mark-exit"
                    className="h-10 px-4 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold inline-flex items-center gap-1.5">
              <LogOut size={13} /> Mark Vehicle Exited
            </button>
          )}
          {canCancel && (
            <button onClick={() => setCancelOpen(true)} data-testid="gp-cancel"
                    className="h-10 px-3 rounded-md border border-[#E4E7EC] text-[12px] font-semibold text-[#e63946] hover:bg-[#e63946]/10 inline-flex items-center gap-1.5">
              <XCircle size={13} /> Cancel
            </button>
          )}
        </div>
      </header>

      {/* Guided workflow strip (bookend showing complete flow) */}
      <WorkflowStrip
        currentStep="gate_pass"
        jobCard={gp.job_card_id ? { id: gp.job_card_id, number: gp.job_card_number } : null}
        invoice={gp.invoice_id ? { id: gp.invoice_id, number: gp.invoice_number } : null}
        gatePass={{ id: gp.id, number: gp.number, status: gp.status }}
        blockReason={
          gp.status === "exited"
            ? `Vehicle has exited. Workflow complete.`
            : gp.status === "cancelled"
              ? `This gate pass was cancelled.`
              : `Ready for exit. Confirm the vehicle handover to close the workflow.`
        }
      />

      {gp.override_used && (
        <section className="rounded-xl bg-[#e63946]/10 border border-[#e63946]/30 p-4 flex items-start gap-3"
                 data-testid="gp-detail-override-banner">
          <AlertTriangle size={16} className="text-[#e63946] shrink-0 mt-0.5" />
          <div>
            <div className="text-[13px] font-bold text-[#e63946]">Payment override recorded</div>
            <div className="text-[12px] text-[#e63946] mt-0.5">
              Reason: <b>{String(gp.override_reason || "").replace(/_/g, " ")}</b>
              {gp.override_note && <> · {gp.override_note}</>}
            </div>
            <div className="text-[11px] text-[#e63946]/80 mt-0.5">Balance at issue: {inr(gp.balance)}</div>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="gp-receiver-card">
            <h3 className="text-[13px] font-bold text-[#1d3557] mb-2">Vehicle handed to</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
              <Info label="Type" value={String(gp.handed_to_type || "").replace(/_/g, " ")} />
              <Info label="Name" value={gp.handed_to_name} />
              <Info label="Phone" value={gp.handed_to_phone || "—"} mono />
              <Info label="Relation / role" value={gp.handed_to_relation || "—"} />
              <Info label="ID reference" value={gp.handed_to_id_ref || "—"} mono />
              <Info label="Authorised by customer" value={gp.authorised_by_customer ? "Yes" : "No"} />
            </div>
          </section>

          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="gp-exit-card">
            <h3 className="text-[13px] font-bold text-[#1d3557] mb-2">Vehicle exit</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 text-[12.5px]">
              <Info label="Odometer at exit" value={gp.odometer_at_exit != null ? `${gp.odometer_at_exit} km` : "—"} mono />
              <Info label="Fuel at exit" value={gp.fuel_at_exit || "—"} />
              <Info label="Security" value={gp.security_person || "—"} />
              <Info label="Exit at" value={gp.exit_at ? new Date(gp.exit_at).toLocaleString() : (gp.vehicle_exited_at ? new Date(gp.vehicle_exited_at).toLocaleString() : "—")} />
            </div>
          </section>

          {(gp.checklist && Object.keys(gp.checklist).length > 0) && (
            <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="gp-checklist-card">
              <h3 className="text-[13px] font-bold text-[#1d3557] mb-2">Checklist</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
                {Object.entries(gp.checklist).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-2 text-[12.5px]">
                    <span className={`w-4 h-4 rounded-sm grid place-items-center ${v ? "bg-[#12B76A] text-white" : "border border-[#E4E7EC]"}`}>
                      {v && <Check size={10} />}
                    </span>
                    <span className={v ? "text-[#1d3557]" : "text-[#a8dadc]"}>
                      {k.replace(/_/g, " ")}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {(gp.customer_remarks || gp.internal_notes) && (
            <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 space-y-2">
              {gp.customer_remarks && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Customer remarks</div>
                  <div className="text-[12.5px] text-[#1d3557]">{gp.customer_remarks}</div>
                </div>
              )}
              {gp.internal_notes && (
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Internal notes</div>
                  <div className="text-[12.5px] text-[#1d3557]">{gp.internal_notes}</div>
                </div>
              )}
            </section>
          )}

          {audit.length > 0 && (
            <section className="bg-white rounded-xl border border-[#E4E7EC] p-4" data-testid="gp-audit">
              <h3 className="text-[13px] font-bold text-[#1d3557] mb-2">Activity ({audit.length})</h3>
              <ul className="space-y-1.5 text-[11.5px] text-[#667085]">
                {audit.map((a) => (
                  <li key={a.id} className="flex gap-2">
                    <span className="font-mono text-[10.5px]">{new Date(a.at).toLocaleTimeString()}</span>
                    <span className="font-semibold text-[#1d3557]">{a.action}</span>
                    <span className="text-[#a8dadc]">·</span>
                    <span>{a.actor_name}</span>
                    {a.note && <span className="text-[#457b9d]">&ldquo;{a.note}&rdquo;</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>

        <aside>
          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 sticky top-4" data-testid="gp-detail-summary">
            <h3 className="text-[13px] font-bold text-[#1d3557] mb-3">Payment snapshot</h3>
            <SumRow label="Invoice total" value={inr(gp.invoice_total)} />
            <SumRow label="Amount received" value={inr(gp.amount_received)} muted />
            <SumRow label="Balance" value={inr(gp.balance)} tone={gp.balance > 0 ? "red" : "green"} big />
            <div className="mt-3">
              <span className="inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider bg-[#12B76A]/12 text-[#0F9256]">
                {String(gp.payment_status || "").toUpperCase()}
              </span>
            </div>
            <div className="mt-3 text-[11px] text-[#667085]">
              Issued {new Date(gp.issued_at || gp.created_at).toLocaleString()}
              {gp.vehicle_exited_at && (<div>Exited {new Date(gp.vehicle_exited_at).toLocaleString()}</div>)}
            </div>
          </section>
        </aside>
      </div>

      {exitOpen && (
        <ExitDialog gp={gp} onClose={() => setExitOpen(false)}
                    onExited={() => { setExitOpen(false); load(); }} />
      )}
      {cancelOpen && (
        <CancelDialog gp={gp} onClose={() => setCancelOpen(false)}
                      onCancelled={() => { setCancelOpen(false); load(); }} />
      )}
    </div>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#667085]">{label}</div>
      <div className={`text-[13px] text-[#1d3557] ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}

function SumRow({ label, value, muted, big, tone }) {
  const cls = big ? "text-[15px] font-bold" : "text-[12.5px]";
  const toneCls = tone === "green" ? "text-[#0F9256]" : tone === "red" ? "text-[#e63946]" : "";
  return (
    <div className={`flex items-center justify-between py-1 ${cls}`}>
      <span className={muted ? "text-[#667085]" : "text-[#1d3557]"}>{label}</span>
      <span className={`font-mono tabular-nums ${toneCls}`}>{value}</span>
    </div>
  );
}

function ExitDialog({ gp, onClose, onExited }) {
  const [odo, setOdo] = useState(gp.odometer_at_exit ?? "");
  const [fuel, setFuel] = useState(gp.fuel_at_exit || "");
  const [security, setSecurity] = useState(gp.security_person || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      await api.post(`/billing/gate-passes/${gp.id}/mark-exit`, {
        odometer_at_exit: odo === "" ? null : Number(odo),
        fuel_at_exit: fuel, security_person: security,
      });
      toast.success("Vehicle marked as exited · job card delivered");
      onExited();
    } catch (e) { setError(e.response?.data?.detail || "Could not mark exit"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="exit-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="exit-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] bg-[#12B76A]/10 flex items-center justify-between">
          <div>
            <h2 className="text-[16px] font-bold text-[#1d3557]">Mark Vehicle Exited</h2>
            <p className="text-[11px] text-[#667085]">Job card will close and the invoice will be marked closed.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-md hover:bg-white grid place-items-center text-[#667085]">
            <X size={17} />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Odometer at exit</span>
            <input type="number" value={odo} onChange={(e) => setOdo(e.target.value)}
                   data-testid="exit-odometer"
                   className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full font-mono" />
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Fuel level</span>
            <select value={fuel} onChange={(e) => setFuel(e.target.value)}
                    data-testid="exit-fuel"
                    className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full bg-white">
              <option value="">Not captured</option>
              {FUEL_LEVELS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Security / gate person</span>
            <input value={security} onChange={(e) => setSecurity(e.target.value)}
                   className="mt-1 h-10 px-2 rounded-md border border-[#E4E7EC] text-[13px] w-full" />
          </label>
          {error && (
            <div className="rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]">{error}</div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Not yet
          </button>
          <button onClick={submit} disabled={saving}
                  data-testid="exit-confirm"
                  className="h-10 px-5 rounded-md bg-[#12B76A] hover:bg-[#0F9256] text-white text-[12.5px] font-semibold disabled:opacity-50 inline-flex items-center gap-1.5">
            <LogOut size={13} /> {saving ? "Closing…" : "Confirm exit"}
          </button>
        </footer>
      </div>
    </div>
  );
}

function CancelDialog({ gp, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    if (!reason.trim()) { setError("Reason is required"); return; }
    setSaving(true); setError(null);
    try {
      await api.post(`/billing/gate-passes/${gp.id}/cancel`, { reason });
      toast.success("Gate pass cancelled");
      onCancelled();
    } catch (e) { setError(e.response?.data?.detail || "Could not cancel"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="gp-cancel-dialog-backdrop">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
           data-testid="gp-cancel-dialog">
        <header className="px-6 py-4 border-b border-[#E4E7EC] bg-[#e63946]/10">
          <h2 className="text-[16px] font-bold text-[#e63946]">Cancel Gate Pass {gp.number}</h2>
          <p className="text-[11px] text-[#667085]">The linked invoice will be released back to its prior status.</p>
        </header>
        <div className="p-5">
          <label className="block">
            <span className="text-[10.5px] uppercase tracking-wider font-semibold text-[#667085]">Reason *</span>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                      data-testid="gp-cancel-reason"
                      className="mt-1 w-full px-2 py-1.5 rounded-md border border-[#E4E7EC] text-[13px]" />
          </label>
          {error && (
            <div className="mt-2 rounded-lg border border-[#e63946]/30 bg-[#e63946]/10 px-3 py-2 text-[12px] text-[#e63946]">{error}</div>
          )}
        </div>
        <footer className="px-6 py-3.5 border-t border-[#E4E7EC] bg-[#F7F9FC] flex justify-end gap-2">
          <button onClick={onClose}
                  className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#667085] hover:bg-white">
            Keep active
          </button>
          <button onClick={submit} disabled={saving}
                  data-testid="gp-cancel-confirm"
                  className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-50">
            {saving ? "Cancelling…" : "Confirm cancel"}
          </button>
        </footer>
      </div>
    </div>
  );
}
